import { useLayoutEffect, useRef, useState } from 'react';

const defaultIntervalTimeout = 30 * 60 * 1000; // 30 min

/**
 * Use this hook to control a 'setInterval' in a component or other hook.
 * Used for polling an API or some other repetitive task on a timer.
 * By default, this ticks every 30min and can be overridden by its only argument.
 *
 * The hook returns:
 *    A refresh method to terminate the current interval and start a new one.
 *    An incrementing number as stateChangeTrigger. This is ues to trigger
 *      a state change in the parent component,
 *      example: in a useEffect dependency or a useState in the parent component.
 * */

export const useInterval = (
  intervalTimeout: number = defaultIntervalTimeout
) => {
  // useRef to store the setInterval outside the lifecycle
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // useState to trigger a refresh of the grid data on interval OR user click
  const [stateChangeTrigger, setStateChangeTrigger] = useState(0);

  const startInterval = () => {
    intervalRef.current = setInterval(() => {
      setStateChangeTrigger(prevState => prevState + 1);
    }, intervalTimeout);
  };
  const cancelInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const refreshInterval = () => {
    cancelInterval();
    setStateChangeTrigger(prevState => prevState + 1);
    startInterval();
  };

  useLayoutEffect(() => {
    startInterval();

    return () => {
      cancelInterval();
    };
  }, []);

  return {
    refreshInterval,
    stateChangeTrigger,
  };
};
